/*global chrome*/
import { action, observable } from 'mobx';
import { api } from '../config';

// Components
import SessionStore from './SessionStore';
import ExamStore from './ExamStore';
import ProctorStore from './ProctorStore';

var obx = observable({
  threads: [],
  // Get all message threads
  getThreads: action(function(backgroundLoad) {
    if(backgroundLoad) {
      SessionStore.startBackgroundLoading('GetThreads');
    } else {
      SessionStore.startLoading('Loading…', 'GetThreads');
    }

    api.get(`/messages?userSid=${SessionStore.userData.userSid}&courseSid=${SessionStore.courseData.sid}`)
      .then(response => {
        obx.threads = response.data.results;

        if(backgroundLoad) {
          SessionStore.finishBackgroundLoading('GetThreads');
        } else {
          SessionStore.finishLoading('GetThreads');
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get thread ID
  activeThread: {},
  getThreadId: action(function(toUserSid, contextType, contextId, toProctor, component, preventReload, proctorLocationSid) {
    obx.activeThread = {};
    obx.threadObject = {};

    api.get(`/messages?userSid=${SessionStore.userData.userSid}`)
      .then(response => {
        let threadId = null;

        // Filter the result by recipient, context type (e.g. examSid), and context id
        const existingThread = response.data.results.filter(function(thread){
          if(toProctor) {
            if(thread.proctorAccountLocation) {
              return thread.proctorAccountUserSid === toUserSid &&
                     thread.proctorAccountLocation.sid === proctorLocationSid &&
                     thread[contextType] === contextId;
            } else {
              return thread.proctorAccountUserSid === toUserSid &&
                     thread[contextType] === contextId;
            }
          } else {
            if(contextType === 'courseSid') {
              return thread.courseSid === contextId &&
                     thread.examConfiguration === null &&
                     thread.examSessionEventReportSid === null &&
                     thread.examSid === null &&
                     thread.proctorApproval === null &&
                     thread.userSid === toUserSid;
            } else {
              return thread[contextType] === contextId &&
                     thread.userSid === toUserSid;
            }
          }
        });

        if(existingThread.length > 0) {
          threadId = existingThread[0].sid;
          obx.activeThread = existingThread[0];
        }

        if(threadId) {
          // Get the actual thread
          obx.getThread(threadId, component, preventReload);
        } else {
          // Otherwise if no thread matches, reset the thread message array
          obx.threadMessages = [];
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Get an individual thread
  threadMessages: [],
  threadObject: {},
  getThread: action(function(sid, component, preventReload) {
    obx.activeThread = {};
    obx.threadObject = {};
    obx.threadMessages = [];

    SessionStore.startBackgroundLoading('GetThread');

    api.get(`/messages/${sid}?includeItems=true`)
      .then(response => {
        obx.threadMessages = response.data.items;
        obx.threadObject = response.data;

        // If there’s a configuration sid, get that data
        if(response.data.examConfigurationSid) {
          ExamStore.getConfigurationData(response.data.examSid, response.data.examConfigurationSid);
        }

        // If there’s an exam sid, get that data
        if(response.data.examSid && !preventReload) {
          ExamStore.getExam(response.data.examSid);
        }

        // If there’s a component, give it the messageSid
        if(component) {
          component.setState({
            messageSid: response.data.sid
          });
        }

        // If there’s a proctor location, get the proctor data
        if(response.data.proctorAccountLocation) {
          ProctorStore.getProctor(response.data.proctorAccountLocation.proctorSid, component);
        }

        SessionStore.finishBackgroundLoading('GetThread');
      })
      .catch(error => {
        SessionStore.throwError(error);
      });

    // Mark the thread as read
    const readUser = {
      userSid: SessionStore.userData.userSid
    }

    api.put(`/messages/${sid}/read`, readUser)
      .then(response => {
        SessionStore.getUnreadCount();
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Create a thread
  createThread: action(function(data, callback) {
    SessionStore.startBackgroundLoading('CreateThread');

    api.post(`/messages`, data)
      .then(response => {
        SessionStore.finishBackgroundLoading('CreateThread');

        if(callback) {
          callback();
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Reply to a thread
  newDates: {},
  replyToThread: action(function(sid, data) {
    SessionStore.startBackgroundLoading('ReplyToThread');

    obx.newDates = {};

    if(data.dateOption1) {
      obx.newDates.dateOption1 = data.dateOption1;
      delete data.dateOption1;
    }

    if(data.dateOption2) {
      obx.newDates.dateOption2 = data.dateOption2;
      delete data.dateOption2;
    }

    if(data.dateOption3) {
      obx.newDates.dateOption3 = data.dateOption3;
      delete data.dateOption3;
    }

    api.post(`/messages/${sid}/items`, data)
      .then(response => {
        SessionStore.finishBackgroundLoading('ReplyToThread');

        if(
          Object.keys(obx.newDates).length > 0 &&
          (obx.newDates.dateOption1 !== obx.threadObject.dateOption1 ||
          obx.newDates.dateOption2 !== obx.threadObject.dateOption2 ||
          obx.newDates.dateOption3 !== obx.threadObject.dateOption3)
        ) {
          obx.updateDates(sid, response.data.messageThreadSid);
        } else {
          obx.getThread(response.data.messageThreadSid);
        }
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Update requested session dates
  updateDates: action(function(sid, threadSid) {
    SessionStore.startBackgroundLoading('UpdateThreadDates');

    api.put(`/messages/${sid}/dates`, obx.newDates)
      .then(response => {
        SessionStore.finishBackgroundLoading('UpdateThreadDates');
        obx.newDates = {};
        obx.getThread(threadSid);
      })
      .catch(error => {
        SessionStore.throwError(error);
      });
  }),
  // Cancel request/clear schedule dates
  cancelRequest: action(function(sid, datesEl) {
    SessionStore.startBackgroundLoading('DeleteRequest');

    api.delete(`/messages/${sid}/dates`)
      .then(response => {
        if(datesEl) {
          // Fade the request out
          datesEl.className += ' fade-out';

          // Then remove it after the fade
          // IE doesn’t support remove, though
          if(document.querySelector('html').className.indexOf('ie-true') < 0) {
            setTimeout(function(){
              datesEl.remove();
            }, 250);
          }

          // Remove the requested dates from the stored thread
          obx.getThread(sid);
        }

        SessionStore.finishBackgroundLoading('DeleteRequest');
      })
      .catch(error => {
        SessionStore.throwError(error);
      })
  }),

  // START EXTENSION MESSAGES
  // Listen for Chrome Extension messages
  ghostRunning: false,
  chromeExtensionMessageListener: (event) => {
    switch(event.data.message) {
      case 'faceProcessorStarted':
        obx.ghostRunning = true;
        const ghostInterval = setInterval(()=> {
          chrome.runtime.sendMessage(SessionStore.extensionInfo.extensionId,
            { message: 'ghostStarted_acknowledgement' },
            response => {
              if(response) {
                clearInterval(ghostInterval)
              }
            }
          )
        }, 1000);
        break;
      default:
        break;
    }
    return;
  },
});

window.addEventListener('message', obx.chromeExtensionMessageListener, false);

export default obx;


// WEBPACK FOOTER //
// ./src/app/stores/MessageStore.js